import {
  Autocomplete,
  Box,
  FormControl,
  ListItem,
  TextField,
  Typography
} from "@mui/material";
import { useEffect } from "react";
import {
  Control,
  Controller,
  FieldValues,
  Path,
  PathValue,
  useFormContext,
  useWatch
} from "react-hook-form";
import { useTranslation } from "react-i18next";

import {
  newSettingsAutocompleteTextFieldSx,
  overviewAutocompleteSx,
  overviewAutocompleteTagsDisplaySx
} from "~/features/preview/workstation/workstation.styles";
import { TemperatureZone } from "~/types/api";

type Props<T extends FieldValues> = {
  control: Control<T>;
  fieldName: Path<T>;
  options: TemperatureZone[];
  disabled?: boolean;
};

export function TempZoneSelect<T extends FieldValues>(props: Props<T>) {
  const { control, fieldName, options, disabled = false } = props;
  const { t } = useTranslation();
  const {
    setError,
    clearErrors,
    formState: { isDirty, errors }
  } = useFormContext<T>();
  const selectedZones =
    (useWatch({ control, name: fieldName }) as TemperatureZone[] | undefined) ??
    [];

  useEffect(() => {
    // eslint-disable-next-line react-you-might-not-need-an-effect/no-event-handler
    if (disabled) {
      clearErrors(fieldName);
      return;
    }
    if (selectedZones.length === 0 && isDirty) {
      setError(fieldName, {
        type: "required",
        message: t("at least one picking temp zone is required")
      });
    } else if (selectedZones.length > 0) {
      clearErrors(fieldName);
    }
  }, [
    clearErrors,
    disabled,
    fieldName,
    isDirty,
    selectedZones.length,
    setError,
    t
  ]);

  const fieldError = errors[fieldName];
  const optionsWithSelected = Array.from(
    new Set([...options, ...selectedZones.filter((zone) => Boolean(zone))])
  );

  return (
    <Box width="100%">
      <FormControl fullWidth size="small" error={Boolean(fieldError)}>
        <Controller
          name={fieldName}
          control={control}
          render={({ field: { onChange, value } }) => {
            const zones = (value as TemperatureZone[] | undefined) ?? [];

            return (
              <Autocomplete
                multiple
                size="small"
                disabled={disabled}
                autoComplete={false}
                disableClearable
                disableCloseOnSelect
                aria-label={t("picking temp zones overview aria label")}
                getOptionLabel={(zone) => t(zone)}
                options={optionsWithSelected}
                sx={overviewAutocompleteSx}
                renderOption={(optionProps, option, state) => (
                  <ListItem {...optionProps} key={`${state.index}-${option}`}>
                    <Typography sx={{ width: "100%" }}>{t(option)}</Typography>
                  </ListItem>
                )}
                renderTags={(tagValue) => (
                  <Box component="span" sx={overviewAutocompleteTagsDisplaySx}>
                    {tagValue.map((zone) => t(zone)).join(", ")}
                  </Box>
                )}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    variant="outlined"
                    error={Boolean(fieldError)}
                    slotProps={{
                      htmlInput: {
                        ...params.inputProps,
                        "aria-label": t(
                          "picking temp zones overview aria label"
                        ),
                        readOnly: true
                      }
                    }}
                    sx={newSettingsAutocompleteTextFieldSx}
                  />
                )}
                value={zones}
                onChange={(_, values) =>
                  onChange(values as PathValue<T, Path<T>>)
                }
              />
            );
          }}
        />
      </FormControl>
    </Box>
  );
}
